import { DateInput, TimeInput, TextInput, SelectInput, NumericInput } from '../inputs';

const PURPOSE_CODE_OPTIONS = [
  { value: '00', label: 'Original Shipment' },
  { value: '01', label: 'Cancellation' },
  { value: '04', label: 'Change/Update' },
  { value: '05', label: 'Replacement' },
];

const TIME_CODE_OPTIONS = [
  { value: 'CT', label: 'Central Time' },
  { value: 'ET', label: 'Eastern Time' },
  { value: 'MT', label: 'Mountain Time' },
  { value: 'PT', label: 'Pacific Time' },
  { value: 'GMT', label: 'Greenwich Mean Time' },
];

const TRANSPORT_METHOD_OPTIONS = [
  { value: 'M', label: 'Motor (Private Carrier)' },
  { value: 'LT', label: 'Less Than Truckload (LTL)' },
  { value: 'TL', label: 'Full Truckload' },
  { value: 'A', label: 'Air Freight' },
  { value: 'S', label: 'Ocean Freight' },
  { value: 'R', label: 'Rail' },
];

// This field is TD1-01, which is X12 element 103 — a composite of a 3-char
// packaging form plus a 2-char material code (CTN25, PLT71). Every value below
// except the CTN25/PLT71 pair is an incomplete composite inherited from the
// original form; they are kept ONLY so documents already holding them still
// render a selected option (a controlled <select> with no matching <option>
// shows blank). Prefer the composite codes for anything new.
const PACKAGING_OPTIONS = [
  { value: 'CTN25', label: 'Cartons' },
  { value: 'PLT71', label: 'Pallets' },
  { value: 'PCS', label: 'Pieces (legacy)' },
  { value: 'PLT', label: 'Pallets (legacy)' },
  { value: 'CTN', label: 'Cartons (legacy)' },
  { value: 'DRM', label: 'Drums (legacy)' },
  { value: 'PKG', label: 'Packages (legacy)' },
];

const CARRIER_QUALIFIER_OPTIONS = [
  { value: '2', label: 'SCAC Code' },
  { value: '1', label: 'Company Code' },
  { value: '4', label: 'IATA Code' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'CC', label: 'Credit Card' },
  { value: 'PP', label: 'Prepaid' },
  { value: 'TP', label: 'Third Party' },
  { value: 'CA', label: 'Collect' },
];

interface HeaderSectionProps {
  data: {
    purposeCode: string;
    shipmentIdentification: string;
    date: string;
    time: string;
    timeCode: string;
    grossWeight: string;
    packagingCode: string;
    ladingQuantity: string;
    carrierIdentificationQualifier: string;
    carrierIdentificationCode: string;
    transportationMethod: string;
    routing: string;
    equipmentDescriptionCode: string;
    equipmentInitial: string;
    equipmentNumber: string;
    shipmentMethodOfPayment: string;
  };
  onChange: (field: string, value: string) => void;
  errors?: Record<string, string>;
}

export function HeaderSection({ data, onChange, errors = {} }: HeaderSectionProps) {
  return (
    <div className="form-compact">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Shipment Information</h3>
        <div className="form-grid-compact grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
          <SelectInput
            label="Purpose Code"
            value={data.purposeCode}
            onChange={(value) => onChange('purposeCode', value)}
            options={PURPOSE_CODE_OPTIONS}
            required
            error={errors.purposeCode}
            helperText="Transaction purpose"
          />
          <TextInput
            label="Shipment ID"
            value={data.shipmentIdentification}
            onChange={(value) => onChange('shipmentIdentification', value)}
            name="shipmentIdentification"
            required
            error={errors.shipmentIdentification}
            placeholder="e.g., 02200043"
            helperText="Unique shipment identifier"
          />
          <DateInput
            label="Shipment Date"
            value={data.date}
            onChange={(value) => onChange('date', value)}
            required
            error={errors.date}
            helperText="Format: MM/DD/YYYY"
          />
          <TimeInput
            label="Shipment Time"
            value={data.time}
            onChange={(value) => onChange('time', value)}
            required
            error={errors.time}
            helperText="Format: HH:MM AM/PM"
          />
          <SelectInput
            label="Time Zone"
            value={data.timeCode}
            onChange={(value) => onChange('timeCode', value)}
            options={TIME_CODE_OPTIONS}
            required
            error={errors.timeCode}
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Weight & Packaging</h3>
        <div className="form-grid-compact grid-cols-1 md:grid-cols-3">
          <NumericInput
            label="Gross Weight"
            value={data.grossWeight}
            onChange={(value) => onChange('grossWeight', value)}
            name="grossWeight"
            unit="LB"
            error={errors.grossWeight}
            helperText="Total shipment weight"
          />
          <SelectInput
            label="Packaging Type"
            value={data.packagingCode}
            onChange={(value) => onChange('packagingCode', value)}
            options={PACKAGING_OPTIONS}
            required
            error={errors.packagingCode}
          />
          <NumericInput
            label="Lading Quantity"
            value={data.ladingQuantity}
            onChange={(value) => onChange('ladingQuantity', value)}
            error={errors.ladingQuantity}
            helperText="Total number of units"
          />
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Carrier & Equipment</h3>
        <div className="form-grid-compact grid-cols-1 md:grid-cols-3 lg:grid-cols-4">
          <SelectInput
            label="Carrier ID Type"
            value={data.carrierIdentificationQualifier}
            onChange={(value) => onChange('carrierIdentificationQualifier', value)}
            options={CARRIER_QUALIFIER_OPTIONS}
            error={errors.carrierIdentificationQualifier}
            helperText="Type of carrier identifier"
          />
          <TextInput
            label="Carrier Code"
            value={data.carrierIdentificationCode}
            onChange={(value) => onChange('carrierIdentificationCode', value)}
            name="carrierIdentificationCode"
            required
            error={errors.carrierIdentificationCode}
            placeholder="e.g., XPOL"
            helperText="SCAC code (required)"
          />
          <TextInput
            label="Carrier Ref # (PRO)"
            value={data.carrierReferenceNumber}
            onChange={(value) => onChange('carrierReferenceNumber', value)}
            name="carrierReferenceNumber"
            required
            error={errors.carrierReferenceNumber}
            placeholder="e.g., PRO123456"
            helperText="PRO or BOL number (required)"
          />
          <SelectInput
            label="Transportation Method"
            value={data.transportationMethod}
            onChange={(value) => onChange('transportationMethod', value)}
            options={TRANSPORT_METHOD_OPTIONS}
            required
            error={errors.transportationMethod}
          />
          <TextInput
            label="Routing"
            value={data.routing}
            onChange={(value) => onChange('routing', value)}
            error={errors.routing}
            placeholder="Optional"
            helperText="Routing information"
          />
          <SelectInput
            label="Payment Method"
            value={data.shipmentMethodOfPayment}
            onChange={(value) => onChange('shipmentMethodOfPayment', value)}
            options={PAYMENT_METHOD_OPTIONS}
            error={errors.shipmentMethodOfPayment}
            helperText="Method of payment"
          />
        </div>
      </div>
    </div>
  );
}
